/**
 * AI Systems pages: the loop diagram and the problem picker; on the Our
 * Thinking pillar pages, the switcher; on About, the team's "Read more".
 *
 * All are rendered complete on the server (Data Readiness active, the
 * first problem selected, the longer bios in the page) so the page reads
 * without this file. The script only moves the highlight and the panel,
 * and folds the bios away until asked for.
 */

(function (Drupal, once) {
  "use strict";

  /**
   * "Five systems, one loop".
   *
   * Hovering, tapping or focusing a node makes it active: its edges and
   * neighbours highlight, the rest dim, the detail strip changes. Hovering
   * one of the five system cards elsewhere on the page does the same for
   * the system in that position.
   *
   * @param {HTMLElement} el
   */
  function initLoop(el) {
    let data;
    try {
      data = JSON.parse(el.dataset.kLoopData || "{}");
    } catch (e) {
      return;
    }
    if (!data.nodes || !data.nodes.length) {
      return;
    }

    const nodes = Array.from(el.querySelectorAll("[data-k-loop-node]"));
    const edges = Array.from(el.querySelectorAll("[data-k-loop-edge]"));
    const detail = el.querySelector("[data-k-loop-detail]");
    const cards = Array.from(
      document.querySelectorAll("[data-k-fgrid] [data-k-loop-index]"),
    );
    const byKey = {};
    data.nodes.forEach((n) => {
      byKey[n.key] = n;
    });

    const activate = (key) => {
      const current = byKey[key];
      if (!current) {
        return;
      }

      const linked = new Set([key]);
      edges.forEach((edge) => {
        const [from, to] = edge.dataset.kLoopEdge.split(":");
        const on = from === key || to === key;
        edge.classList.toggle("is-on", on);
        if (on) {
          linked.add(from);
          linked.add(to);
        }
      });

      nodes.forEach((node) => {
        const k = node.dataset.kLoopNode;
        node.classList.remove("is-active", "is-linked", "is-dim");
        node.classList.add(
          k === key ? "is-active" : linked.has(k) ? "is-linked" : "is-dim",
        );
        node.setAttribute("aria-pressed", k === key ? "true" : "false");
      });

      if (detail) {
        ["n", "tag", "name", "role", "from", "to"].forEach((field) => {
          const slot = detail.querySelector(`[data-k-loop-field="${field}"]`);
          if (slot) {
            const value = current[field] || "";
            slot.textContent = field === "tag" ? value.toUpperCase() : value;
          }
        });
      }

      const index = data.nodes.indexOf(current) + 1;
      cards.forEach((card) => {
        card.classList.toggle(
          "is-active",
          parseInt(card.dataset.kLoopIndex, 10) === index,
        );
      });
    };

    nodes.forEach((node) => {
      const key = node.dataset.kLoopNode;
      node.addEventListener("mouseenter", () => activate(key));
      node.addEventListener("focus", () => activate(key));
      node.addEventListener("click", () => activate(key));
      node.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate(key);
        }
      });
    });

    cards.forEach((card) => {
      card.addEventListener("mouseenter", () => {
        const node = data.nodes[parseInt(card.dataset.kLoopIndex, 10) - 1];
        if (node) {
          activate(node.key);
        }
      });
    });

    activate(data.active || data.nodes[0].key);
  }

  /**
   * "Where is your AI stuck?" - a radio group of problems.
   *
   * @param {HTMLElement} el
   */
  function initPicker(el) {
    const options = Array.from(el.querySelectorAll("[data-k-picker-option]"));
    const panels = Array.from(el.querySelectorAll("[data-k-picker-panel]"));
    if (!options.length) {
      return;
    }

    const select = (id, focus) => {
      options.forEach((option) => {
        const on = option.dataset.kPickerOption === id;
        option.classList.toggle("is-selected", on);
        option.setAttribute("aria-checked", on ? "true" : "false");
        option.tabIndex = on ? 0 : -1;
        if (on && focus) {
          option.focus();
        }
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.kPickerPanel !== id;
      });
    };

    // Stacked (phones), the answer sits below the whole list, so a tap
    // would change something off screen. Bring it into view.
    const list = el.querySelector(".k-picker__options");
    const result = el.querySelector(".k-picker__result");
    const reveal = () => {
      if (!list || !result || result.offsetTop <= list.offsetTop) {
        return;
      }
      if (result.getBoundingClientRect().top > window.innerHeight * 0.6) {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        result.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      }
    };

    options.forEach((option, i) => {
      option.addEventListener("click", () => {
        select(option.dataset.kPickerOption, false);
        reveal();
      });
      option.addEventListener("keydown", (event) => {
        let next = null;
        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
          next = options[(i + 1) % options.length];
        } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
          next = options[(i - 1 + options.length) % options.length];
        } else if (event.key === " " || event.key === "Enter") {
          next = option;
        }
        if (next) {
          event.preventDefault();
          select(next.dataset.kPickerOption, next !== option);
        }
      });
    });
  }

  /**
   * Pillar switcher: on a phone the tabs scroll sideways, so the current
   * one is brought into view rather than left off the right edge.
   *
   * @param {HTMLElement} el
   *   The scrolling row (.k-loopstrip__inner).
   */
  function initTabs(el) {
    const current = el.querySelector(".k-loopstrip__tab.is-current");
    if (!current || el.scrollWidth <= el.clientWidth) {
      return;
    }
    const left = current.offsetLeft - (el.clientWidth - current.offsetWidth) / 2;
    el.scrollLeft = Math.max(0, left);
  }

  /**
   * The team (About): "Read more" shows the longer bio, and becomes "Show
   * less". The bio is hidden by CSS only once the html element carries the
   * "js" class, so it reads in full without this script.
   *
   * @param {HTMLElement} el
   */
  function initBio(el) {
    const toggle = el.querySelector("[data-k-bio-toggle]");
    const more = el.querySelector("[data-k-bio-more]");
    if (!toggle || !more) {
      return;
    }
    toggle.addEventListener("click", () => {
      const open = !more.classList.contains("is-open");
      more.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      toggle.textContent = open ? toggle.dataset.labelOpen : toggle.dataset.labelClosed;
    });
  }

  Drupal.behaviors.keidoSystems = {
    attach(context) {
      once("keidoLoop", "[data-k-loop]", context).forEach(initLoop);
      once("keidoPicker", "[data-k-picker]", context).forEach(initPicker);
      once("keidoTabs", ".k-loopstrip--tabs .k-loopstrip__inner", context).forEach(initTabs);
      once("keidoBio", "[data-k-bio]", context).forEach(initBio);
    },
  };
})(Drupal, once);
